/* Copyright (c) 2023, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable } from 'rxjs';

export enum StateMachineErrorCodes {
    // Setup Errors
    InvalidStates = 'InvalidStates',
    InvalidTransitions = 'InvalidTransitions',
    InvalidStartState = 'InvalidStartState',
    InvalidEndState = 'InvalidEndState',
    InvalidWildcardState = 'InvalidWildcardState',
    // Transition Errors
    TransitionFromEndState = 'TransitionFromEndState',
    TransitionNotFound = 'TransitionNotFound',
    TransitionError = 'TransitionError',
    // Validation Errors
    UnreachableState = 'UnreachableState',
    InvalidStateName = 'InvalidStateName',
    AmbiguousTransition = 'AmbiguousTransition',
    WildcardTargetState = 'WildcardTargetState',
    MultipleWildcardTransitions = 'MultipleWildcardTransitions'
}

/** @description Subclassed error class for more detailed error reporting. */
export class StateMachineError extends Error {
    /** @property Unique name for state machine error. */
    readonly name = 'StateMachineError';
    /** @property User provided tag per instance of the state machine, part of every error it throws. */
    tag: string;
    /** @property Error code to identify the exact type of error. */
    code: StateMachineErrorCodes;
    /** @property Additional error data if required. */
    data?: any;

    constructor(tag: string, code: StateMachineErrorCodes, message: string, data?: any) {
        super(`${tag} - ${code} - ${message}`);
        this.tag = tag;
        this.code = code;
        this.data = data;
    }
}

export type State = string;
export type Action = string;
export const WildcardState = '**';

/**
 * @description List of all states in the state machine.
 * Each `key` is the name of a valid state.
 * Each `value` is the pair of actions to be performed while entering or exiting that state.
 */
export interface States<T extends object> {
    [state: string]: {
        /**
         * @description A function that takes a `T` as an argument and returns an `Observable<T>`.
         * In the state transition flow, this represent's the action to be performed while entering
         * this State.
         * @param stateData Data of type `T` which represents state variables. This parameter is
         * the output of the previous state's `onExit` function, and is passed in by the state machine
         * internally.
         * @returns An `Observable<T>` which represents the latest state of the machine. This is the
         * value received by the `subscriber` of `StateMachine.on()`.
         * @default x => of(x)
         */
        onEnter?: (stateData: T) => Observable<T>;
        /**
         * @description A function that takes a `T` as an argument and returns an `Observable<T>`.
         * In the state transition flow, this represent's the action to be performed while exiting
         * this State.
         * @param stateData Data of type `T` which represents state variables. This parameter is
         * the input to the `StateMachine.on()` function, and is passed in by the state machine
         * internally.
         * @returns An `Observable<T>` which represents the latest state of the machine. This is the
         * value received by the `onEnter` function of the next state.
         * @default x => of(x)
         */
        onExit?: (stateData: T) => Observable<T>;
    };
}

/**
 * @description An individual transition, defined `from` a current state `to` a target state.
 */
export interface Transition {
    /** @description Current state for the transition. */
    from: State;
    /** @description Target state for the transition. */
    to: State;
}

/**
 * @description List of all valid transitions in the state machine.
 * Each `key` is an action that can be performed on the state machine.
 * Each `value` is a list of valid transitions for that action.
 */
export interface Transitions {
    [action: string]: Transition[];
}

export interface StateMachineMetadata<T extends object> {
    tag: string;
    states: States<T>;
    transitions: Transitions;
    startState: State;
    endState: State;
}
